import { getUrlTrackingParams } from './functions';
import { hasStorageAvailable } from './storage';

interface IParamsObject {
  [key: string]: string;
}

const sessionStorageTrackingParams = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'detail',
  'wbraid',
  'gh_src',
] as const;

const localStorageTrackingParams = [
  'irclickid',
] as const;

/**
 * @description Retrieves tracking query parameters from sessionStorage
 * @param {object} paramsObject - Object containing key value pairs of tracking query parameters
 * @returns {object} Object containing key value pairs of tracking query parameters
 */
export const getSessionStorageTrackingParams = (paramsObject: IParamsObject): IParamsObject => {
  if (hasStorageAvailable('sessionStorage')) {
    sessionStorageTrackingParams.forEach((param) => {
      const storedParam = sessionStorage.getItem(param);
      if (storedParam) {
        paramsObject[param] = JSON.parse(storedParam);
      }
    });
  }
  return paramsObject;
};

/**
 * @description Retrieves tracking query parameters from localStorage
 * @param {object} paramsObject - Object containing key value pairs of tracking query parameters
 * @returns {object} Object containing key value pairs of tracking query parameters
 */
export const getLocalStorageTrackingParams = (paramsObject: IParamsObject): IParamsObject => {
  if (hasStorageAvailable('localStorage')) {
    localStorageTrackingParams.forEach((param) => {
      const storedParam = localStorage.getItem(param);
      if (storedParam) {
        paramsObject[param] = JSON.parse(storedParam);
      }
    });
  }
  return paramsObject;
};

export const getDefaultParamsObject = (): IParamsObject => {
  return {
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
    detail: '',
    wbraid: '',
    gh_src: '',
    irclickid: '',
  };
};

/**
 * @description Retrieves tracking query parameters from sessionStorage and localStorage
 * @returns {object} Object containing key value pairs of tracking query parameters
 */
export const getStoredTrackingParams = (): IParamsObject => {
  let paramsObject = getDefaultParamsObject();

  try {
    paramsObject = getSessionStorageTrackingParams(paramsObject);
    paramsObject = getLocalStorageTrackingParams(paramsObject);
  } catch {
    return paramsObject;
  }

  return paramsObject;
};

/**
 * @description Stores tracking query parameters in sessionStorage
 * @param {object} paramsObject - Object containing key value pairs of tracking query parameters
 */
export const storeSessionStorageTrackingParams = (paramsObject: IParamsObject) => {
  sessionStorageTrackingParams.forEach((param) => {
    if (paramsObject[param]) {
      sessionStorage.setItem(param, JSON.stringify(paramsObject[param]));
    }
  });
};

/**
 * @description Stores tracking query parameters in localStorage
 * @param {object} paramsObject - Object containing key value pairs of tracking query parameters
 */
export const storeLocalStorageTrackingParams = (urlTrackingParams: IParamsObject) => {
  if (hasStorageAvailable('localStorage')) {
    localStorageTrackingParams.forEach((param) => {
      if (urlTrackingParams[param]) {
        localStorage.setItem(param, JSON.stringify(urlTrackingParams[param]));
      }
    });
  }
};

/**
 * @description Stores tracking query parameters in sessionStorage and localStorage
 */
export const storeUrlTrackingParams = () => {
  const urlTrackingParams = getUrlTrackingParams();

  storeSessionStorageTrackingParams(urlTrackingParams);
  storeLocalStorageTrackingParams(urlTrackingParams);
};
